import React, { useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSyncAlt, faTrashAlt } from "@fortawesome/free-solid-svg-icons";

import api from "../../api/http";
import "../../styles/admin/ServiciiManager.css";

const ServiciiManager = () => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);

  // create
  const [newService, setNewService] = useState("");
  const [creating, setCreating] = useState(false);

  // edit
  const [editId, setEditId] = useState(null);
  const [editValue, setEditValue] = useState("");
  const [saving, setSaving] = useState(false);

  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  const isLoggedIn = Boolean(sessionStorage.getItem("access"));

  useEffect(() => {
    if (!message) return;
    const t = setTimeout(() => setMessage(""), 5000);
    return () => clearTimeout(t);
  }, [message]);

  const parseApiError = (err) => {
    const data = err?.response?.data;

    if (!data) return err?.message || "A apărut o eroare.";
    if (typeof data === "string") return data;
    if (Array.isArray(data)) return data[0] || "A apărut o eroare.";
    if (data.detail) return data.detail;

    if (typeof data === "object") {
      const k = Object.keys(data)[0];
      const v = data[k];
      if (Array.isArray(v)) return v[0];
      if (typeof v === "string") return v;
    }

    return "A apărut o eroare.";
  };

  const normalizeList = (data) => {
    if (Array.isArray(data?.results)) return data.results;
    if (Array.isArray(data)) return data;
    return [];
  };

  const refreshData = async () => {
    setError("");
    try {
      const res = await api.get("/about/servicii");
      setServices(normalizeList(res.data));
    } catch (err) {
      setServices([]);
      setError(parseApiError(err));
    }
  };

  useEffect(() => {
    (async () => {
      setLoading(true);
      await refreshData();
      setLoading(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleServiceSubmit = async (e) => {
    e.preventDefault();
    setMessage("");
    setError("");

    const text = newService.trim();
    if (!text) {
      setError("Introduceți un serviciu.");
      return;
    }

    setCreating(true);
    try {
      await api.post("/about/servicii-create", { serviciu: text });
      setNewService("");
      setMessage("Serviciu adăugat cu succes.");
      await refreshData();
    } catch (err) {
      setError(parseApiError(err));
    } finally {
      setCreating(false);
    }
  };

  const openEdit = (service) => {
    setMessage("");
    setError("");
    setEditId(service.id);
    setEditValue(service.serviciu || "");
  };

  const closeEdit = () => {
    setEditId(null);
    setEditValue("");
  };

  const saveEdit = async (serviceId) => {
    setMessage("");
    setError("");

    const text = editValue.trim();
    if (!text) {
      setError("Textul serviciului nu poate fi gol.");
      return;
    }

    setSaving(true);
    try {
      await api.put(`/about/servicii/${serviceId}`, { serviciu: text });

      setServices((prev) =>
        prev.map((s) => (s.id === serviceId ? { ...s, serviciu: text } : s))
      );

      setMessage("Serviciu actualizat.");
      closeEdit();

      await refreshData();
    } catch (err) {
      setError(parseApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const handleServiceDelete = async (serviceId) => {
    setMessage("");
    setError("");

    const ok = window.confirm("Sigur vrei să ștergi acest serviciu?");
    if (!ok) return;

    try {
      await api.delete(`/about/servicii/${serviceId}`);

      setServices((prev) => prev.filter((s) => s.id !== serviceId));
      setMessage("Serviciu șters.");

      await refreshData();
    } catch (err) {
      setError(parseApiError(err));
    }
  };

  const onEditKeyDown = (e, serviceId) => {
    if (e.key === "Escape") closeEdit();
    if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) saveEdit(serviceId);
  };

  return (
    <div className="sr-servicii">

        {message && <p className="sr-servicii__msg sr-servicii__msg--ok">{message}</p>}
        {error && <p className="sr-servicii__msg sr-servicii__msg--err">{error}</p>}

      {isLoggedIn && (
        <div className="sr-servicii__create">
          <form className="sr-servicii__createForm" onSubmit={handleServiceSubmit}>
            <textarea
              className="sr-servicii__textarea"
              value={newService}
              onChange={(e) => setNewService(e.target.value)}
              placeholder="Introduceți un serviciu nou"
              rows={3}
              disabled={creating}
            />
            <button className="sr-servicii__btn" type="submit" disabled={creating}>
              {creating ? "Se adaugă..." : "Adaugă serviciu"}
            </button>
          </form>
        </div>
      )}

      <div className="sr-servicii__list">
        <div className="sr-servicii__items">
          {loading ? (
            <p className="sr-servicii__loading">Loading...</p>
          ) : services.length === 0 ? (
            <p className="sr-servicii__empty">Nu există servicii.</p>
          ) : (
            services
              .slice()
              .reverse()
              .map((service) => (
                <div className="sr-servicii__item" key={service.id}>
                  {editId === service.id ? (
                    <div className="sr-servicii__edit">
                      <textarea
                        className="sr-servicii__textarea"
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        onKeyDown={(e) => onEditKeyDown(e, service.id)}
                        placeholder="Actualizează serviciu"
                        rows={3}
                        disabled={saving}
                      />

                      <div className="sr-servicii__actions">
                        <button
                          className="sr-servicii__btn"
                          type="button"
                          onClick={() => saveEdit(service.id)}
                          disabled={saving}
                        >
                          {saving ? "Se salvează..." : "Salvează"}
                        </button>

                        <button
                          className="sr-servicii__btn sr-servicii__btn--danger"
                          type="button"
                          onClick={closeEdit}
                          disabled={saving}
                        >
                          Close
                        </button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <p className="sr-servicii__text">{service.serviciu}</p>

                      {isLoggedIn && (
                        <div className="sr-servicii__icons">
                          <button
                            type="button"
                            className="sr-servicii__iconBtn sr-servicii__iconBtn--edit"
                            onClick={() => openEdit(service)}
                            title="Edit"
                          >
                            <FontAwesomeIcon icon={faSyncAlt} />
                          </button>

                          <button
                            type="button"
                            className="sr-servicii__iconBtn sr-servicii__iconBtn--danger"
                            onClick={() => handleServiceDelete(service.id)}
                            title="Delete"
                          >
                            <FontAwesomeIcon icon={faTrashAlt} />
                          </button>
                        </div>
                      )}
                    </>
                  )}
                </div>
              ))
          )}
        </div>
      </div>
    </div>
  );
};

export default ServiciiManager;